/**
 * useNumPadInput — общая логика цифрового ввода NumPad-шита и NumPadPop
 * (эталоны design-references/numpad.js и numpad-pop.js:41-55, семантика
 * идентична, A27/A42): digits-строка без ведущих нулей, fresh (первая
 * цифра затирает открытое значение), лимит 7 цифр и max → bump (хинт
 * «макс. …» на 1400мс + onBump для shake-анимации хоста), ⌫ клик —
 * backspace, long-press 480мс — полная очистка (последующий click
 * подавлен). Пусто = 0 при коммите (val()); формат денег — ₽ + NBSP-
 * группировка тысяч (grp). Хост рендерит превью/хинт сам — стили у шита
 * и попа разные.
 *
 * UC-расширение по эталону numpad-pop.js v7 (N:56-65,71) — строго
 * аддитивно: decimals>0 разрешает клавишу «.» (дробь до N знаков,
 * max-guard через parseFloat, val() → float), suffix («UC») попадает
 * в хинт «макс. …» ПОСЛЕ числа вместо ₽-префикса. Без decimals/suffix
 * поведение байт-в-байт прежнее (digits никогда не содержит «.»).
 */
import { useRef, useState } from "react";

export type NumPadMode = "money" | "int";

/** decimals-опция → эффективная дробность (0 = int-семантика как раньше). */
const decOf = (o: NumPadInputOpts): number =>
  o.decimals && o.decimals > 0 ? o.decimals : 0;

/** NBSP для группировки тысяч и юнитов. */
export const NB = " ";
/** Группировка тысяч: 200000 → «200 000» (NBSP). */
export const grp = (s: string): string => s.replace(/\B(?=(\d{3})+(?!\d))/g, NB);

export function parseDigits(v: number | string | undefined, decimals = 0): string {
  const n = decimals > 0 ? parseFloat(String(v)) : parseInt(String(v), 10);
  return !n || Number.isNaN(n) ? "" : String(n);
}

export interface NumPadInputOpts {
  mode: NumPadMode;
  /** Юнит int-режима («чел», «мин») — попадает в хинт «макс. …». */
  unit?: string | null;
  max?: number | null;
  /** Дробь до N знаков + клавиша «.» (эталон numpad-pop.js v7); val() → float. */
  decimals?: number;
  /** Суффикс ПОСЛЕ числа вместо ₽-префикса («UC») — в хинте «макс. …». */
  suffix?: string | null;
  /** Отказ ввода (>7 цифр или > max) — хост дёргает shake-анимацию. */
  onBump?: () => void;
}

export interface NumPadInput {
  digits: string;
  hint: { text: string; on: boolean };
  /** Текущее значение; пусто = 0 (эталон numpad-pop.js:41). */
  val: () => number;
  /** Пересброс на открытие/смену ячейки: fresh при непустом значении. */
  init: (value?: number | string) => void;
  /** Прямая установка (пресеты шита): fresh снят, хинт скрыт. */
  set: (d: string | number) => void;
  append: (d: string) => void;
  delDown: () => void;
  delUp: () => void;
  delClick: () => void;
}

const HINT_MS = 1400;
const DEL_LP_MS = 480;
const MAX_LEN = 7;

export function useNumPadInput(opts: NumPadInputOpts): NumPadInput {
  const [digits, setDigits] = useState("");
  /* Синхронное зеркало digits: тапы быстрее ре-рендера читают свежее значение, не замыкание. */
  const digitsRef = useRef("");
  const write = (nd: string) => {
    digitsRef.current = nd;
    setDigits(nd);
  };
  const [hint, setHint] = useState({ text: "", on: false });
  const freshRef = useRef(false);
  const hintT = useRef(0);
  const delT = useRef(0);
  const delFired = useRef(false);
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const hideHint = () => setHint((h) => (h.on ? { ...h, on: false } : h));

  const bump = () => {
    const o = optsRef.current;
    o.onBump?.();
    if (o.max != null) {
      const t = o.suffix
        ? `${o.max}${NB}${o.suffix}`
        : o.mode === "money"
          ? `₽${grp(String(o.max))}`
          : `${o.max}${o.unit ? NB + o.unit : ""}`;
      setHint({ text: `макс. ${t}`, on: true });
      window.clearTimeout(hintT.current);
      hintT.current = window.setTimeout(hideHint, HINT_MS);
    }
  };

  const append = (k: string) => {
    const o = optsRef.current;
    const dec = decOf(o);
    let base = digitsRef.current;
    if (freshRef.current) {
      base = "";
      freshRef.current = false;
    }
    /* Клавиша «.» — только при decimals; повторная точка = bump (N:57-59). */
    if (k === ".") {
      if (!dec) return;
      if (base.includes(".")) return bump();
      write((base || "0") + ".");
      return;
    }
    const nd = (base + k).replace(/^0+(?=\d)/, "");
    const di = nd.indexOf(".");
    if (di < 0) {
      /* Целая часть: лимит 7 цифр + max (parseInt ≡ parseFloat на цифрах). */
      if (nd.length > MAX_LEN || (o.max != null && parseInt(nd, 10) > o.max)) return bump();
    } else {
      /* Дробная часть: до dec знаков; max-guard по parseFloat (N:61-64). */
      if (nd.length - di - 1 > dec || (o.max != null && parseFloat(nd) > o.max)) return bump();
    }
    write(nd);
    hideHint();
  };

  const delDown = () => {
    delFired.current = false;
    delT.current = window.setTimeout(() => {
      delFired.current = true;
      write("");
      freshRef.current = false;
      hideHint();
    }, DEL_LP_MS);
  };
  const delUp = () => window.clearTimeout(delT.current);
  const delClick = () => {
    if (delFired.current) {
      delFired.current = false; // long-press глушит последующий click
      return;
    }
    freshRef.current = false;
    write(digitsRef.current.slice(0, -1));
    hideHint();
  };

  const init = (value?: number | string) => {
    const d = parseDigits(value, decOf(optsRef.current));
    write(d);
    freshRef.current = d !== "";
    setHint({ text: "", on: false });
  };

  const set = (d: string | number) => {
    write(String(d));
    freshRef.current = false;
    hideHint();
  };

  return {
    digits,
    hint,
    /* Хвостовая точка отрезается; float только при decimals (N:71). */
    val: () => {
      const raw = (digitsRef.current || "0").replace(/\.$/, "");
      return decOf(optsRef.current) ? parseFloat(raw) || 0 : parseInt(raw, 10) || 0;
    },
    init,
    set,
    append,
    delDown,
    delUp,
    delClick,
  };
}
